import React, { useState, useEffect } from 'react';
import { ECareLogo } from '../../components/ui/Logo';
import Button from '../../components/ui/Button';
import { colors, createColoredShadow } from '../../constants/colors';
import { useTaxonomyContext } from '../../context/TaxonomyContext';
import styles from './UpdateGoalsPage.module.css';

const UpdateGoalsPage = ({ currentGoals = [], onUpdateGoals, onCancel }) => {
	const { domainTree } = useTaxonomyContext();
	const [selectedOptions, setSelectedOptions] = useState(new Set());

	// Build grouped options from API domain tree: { domainKey: { title, options[] } }
	const getFlowOptions = () => {
		const flowOptions = {};
		domainTree.forEach((domain) => {
			const options = [];
			domain.sub_domains.forEach((sub) => {
				sub.taxonomy_terms.forEach((term) => {
					options.push({
						id: `term_${term.id}`,
						name: term.name,
					});
				});
			});
			if (options.length > 0) {
				flowOptions[`domain_${domain.id}`] = {
					title: domain.name,
					options,
				};
			}
		});
		return flowOptions;
	};

	const flowOptions = getFlowOptions();

	// Initialize selected options based on current goals
	useEffect(() => {
		const initialSelected = new Set(currentGoals.map((goal) => goal.id));
		setSelectedOptions(initialSelected);
	}, [currentGoals]);

	const handleToggleOption = (optionId) => {
		const newSelected = new Set(selectedOptions);
		if (newSelected.has(optionId)) {
			newSelected.delete(optionId);
		} else {
			newSelected.add(optionId);
		}
		setSelectedOptions(newSelected);
	};

	const handleSaveChanges = () => {
		const updatedGoals = [];
		// A goal that survives the edit is the same goal: it keeps the id the
		// server knows it by and the rank it was given. Rebuilding it from the
		// taxonomy alone dropped both, which blanked the priority badges and
		// emptied the journey goal filter until the refetch landed, and left the
		// caller with no ranks to close a gap with.
		const existing = new Map(currentGoals.map((goal) => [goal.id, goal]));

		Array.from(selectedOptions).forEach((termId) => {
			outer: for (const domain of domainTree) {
				for (const sub of domain.sub_domains) {
					const term = sub.taxonomy_terms.find(
						(t) => `term_${t.id}` === termId
					);
					if (term) {
						const previous = existing.get(termId);
						updatedGoals.push({
							id: termId,
							name: term.name,
							smart_watch: false,
							goal_description: term.goal_setting_message || '',
							short_description: term.short_description || '',
							goal_setting_id: previous?.goal_setting_id,
							priority_rank: previous?.priority_rank ?? 0,
						});
						break outer;
					}
				}
			}
		});

		onUpdateGoals(updatedGoals);
	};

	// Card styling similar to GoalCard
	const cardColor = colors.coral;
	const cardStyles = {
		borderColor: cardColor,
		boxShadow: createColoredShadow
			? createColoredShadow(cardColor, 0.4)
			: `0 8px 25px -5px ${cardColor}66, 0 10px 10px -5px ${cardColor}40`,
		'--card-color': cardColor,
		'--card-shadow-hover': createColoredShadow
			? createColoredShadow(cardColor, 0.6)
			: `0 20px 35px -5px ${cardColor}99, 0 15px 15px -5px ${cardColor}60`,
	};

	return (
		<div className={styles.container}>
			{/* This page renders outside AppLayout, which is where the padding
			    that clears the fixed shared Banner lives — so the flow pages
			    carry their own banner, in normal flow. */}
			<div className={styles.blueBanner}>
				<div className={styles.bannerContent}>
					<ECareLogo size='medium' className={styles.bannerLogo} />
					{onCancel && (
						<button
							onClick={onCancel}
							className={styles.cancelButton}
							aria-label='Cancel goal update'
						>
							Close
						</button>
					)}
				</div>
			</div>

			{/* Main content */}
			<div className={styles.content}>
				<h1 className={styles.title}>Update Goals</h1>

				<div className={styles.cardsContainer}>
					{Object.entries(flowOptions).map(([flowKey, flowData]) => (
						<div key={flowKey} className={styles.updateCard} style={cardStyles}>
							<h2 className={styles.cardTitle}>{flowData.title}</h2>

							<div className={styles.optionsList}>
								{flowData.options.map((option) => (
									<div key={option.id} className={styles.optionItem}>
										<label className={styles.optionLabel}>
											<span className={styles.optionName}>{option.name}</span>
											<div className={styles.toggleContainer}>
												<input
													type='checkbox'
													className={styles.toggle}
													checked={selectedOptions.has(option.id)}
													onChange={() => handleToggleOption(option.id)}
												/>
												<span className={styles.toggleSlider}></span>
											</div>
										</label>
									</div>
								))}
							</div>
						</div>
					))}
				</div>

				<div className={styles.actionButtons}>
					<Button
						onClick={handleSaveChanges}
						variant='primary'
						size='large'
						className={styles.saveButton}
					>
						Save Changes
					</Button>
				</div>
			</div>
		</div>
	);
};

export default UpdateGoalsPage;
